
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { Download, FileText, Shield, Cpu, HardDrive, Monitor } from "lucide-react";

const TechnicalSpecs = () => {
  return (
    <Layout>
      <Helmet>
        <title>Technical Specifications - Device Specs & Manuals | TrueCore Gadgets</title>
        <meta name="description" content="Download technical specifications, user manuals, and certification documents for TrueCore tablets, PCs, and smart devices. BIS certified, quality assured." />
        <meta name="keywords" content="TrueCore technical specs, device specifications, user manuals, BIS certification, product documentation, tablet specs, PC specifications" />
        
        <link rel="canonical" href="https://www.truecoregadgets.com/technical-specifications" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Technical Specifications - TrueCore Gadgets" />
        <meta property="og:description" content="Complete technical specifications and documentation for all TrueCore devices." />
        <meta property="og:url" content="https://www.truecoregadgets.com/technical-specifications" />
        <meta property="og:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      </Helmet>

      <div className="container mx-auto py-12 px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-6">Technical Specifications</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive technical documentation, specifications, and certification details for all TrueCore products
          </p>
        </div>

        {/* Product Categories */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {/* Tablets */}
          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-6">
              <Monitor className="h-8 w-8 text-primary mr-3" />
              <h2 className="text-xl font-bold">Android Tablets</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Display</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• 10.1" IPS LCD Display</li>
                  <li>• 1920 x 1200 Resolution</li>
                  <li>• Multi-touch Capacitive</li>
                  <li>• Gorilla Glass Protection</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Performance</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Octa-core ARM Processor</li>
                  <li>• 4GB RAM / 64GB Storage</li>
                  <li>• Android 12 / Linux Support</li>
                  <li>• GPU: Mali-G57 MC2</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Connectivity</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Wi-Fi 802.11ac</li>
                  <li>• Bluetooth 5.0</li>
                  <li>• 4G LTE Optional</li>
                  <li>• USB-C, microHDMI</li>
                </ul>
              </div>
            </div>
          </div>

          {/* PCs */}
          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-6">
              <Cpu className="h-8 w-8 text-accent mr-3" />
              <h2 className="text-xl font-bold">Custom PCs</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Processor</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Intel Core i5/i7 Series</li>
                  <li>• AMD Ryzen 5/7 Options</li>
                  <li>• Up to 4.7GHz Boost Clock</li>
                  <li>• Multi-core Architecture</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Memory & Storage</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• 8GB-32GB DDR4 RAM</li>
                  <li>• 256GB-2TB SSD</li>
                  <li>• Optional HDD Storage</li>
                  <li>• NVMe M.2 Support</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Graphics</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Integrated Intel/AMD</li>
                  <li>• Dedicated GPU Options</li>
                  <li>• 4K Display Support</li>
                  <li>• Multiple Monitor Setup</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Smart Gadgets */}
          <div className="bg-card p-6 rounded-lg shadow-md border">
            <div className="flex items-center mb-6">
              <HardDrive className="h-8 w-8 text-secondary mr-3" />
              <h2 className="text-xl font-bold">Smart Gadgets</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">IoT Devices</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• ARM Cortex-M Series</li>
                  <li>• Wi-Fi/Bluetooth/LoRa</li>
                  <li>• Low Power Design</li>
                  <li>• Cloud Integration</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Embedded Systems</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Real-time OS Support</li>
                  <li>• Industrial Grade</li>
                  <li>• Custom Firmware</li>
                  <li>• Extended Temperature</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Interfaces</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• UART, SPI, I2C</li>
                  <li>• GPIO, ADC, PWM</li>
                  <li>• USB, Ethernet</li>
                  <li>• Custom Connectors</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="bg-muted/50 rounded-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8 flex items-center justify-center">
            <Shield className="h-8 w-8 text-primary mr-3" />
            Certifications & Compliance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-primary text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="font-bold mb-2">BIS Certified</h3>
              <p className="text-sm text-muted-foreground">Bureau of Indian Standards compliance</p>
            </div>
            <div className="text-center">
              <div className="bg-accent text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="font-bold mb-2">ISO 9001:2015</h3>
              <p className="text-sm text-muted-foreground">Quality management systems</p>
            </div>
            <div className="text-center">
              <div className="bg-secondary text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="font-bold mb-2">CE Marking</h3>
              <p className="text-sm text-muted-foreground">European conformity standards</p>
            </div>
            <div className="text-center">
              <div className="bg-primary text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="font-bold mb-2">RoHS Compliant</h3>
              <p className="text-sm text-muted-foreground">Environmental safety standards</p>
            </div>
          </div>
        </div>

        {/* Documentation Downloads */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Documentation & Downloads</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md border">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <FileText className="h-6 w-6 text-primary mr-3" />
                User Manuals
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                  <span>Android Tablet User Manual</span>
                  <button className="text-primary hover:text-primary/80">
                    <Download className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                  <span>Custom PC Setup Guide</span>
                  <button className="text-primary hover:text-primary/80">
                    <Download className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                  <span>IoT Device Configuration</span>
                  <button className="text-primary hover:text-primary/80">
                    <Download className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-card p-6 rounded-lg shadow-md border">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Shield className="h-6 w-6 text-primary mr-3" />
                Certificates
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                  <span>BIS Registration Certificate</span>
                  <button className="text-primary hover:text-primary/80">
                    <Download className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                  <span>ISO 9001:2015 Certificate</span>
                  <button className="text-primary hover:text-primary/80">
                    <Download className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                  <span>CE Declaration of Conformity</span>
                  <button className="text-primary hover:text-primary/80">
                    <Download className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Support */}
        <div className="bg-primary/10 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Need Technical Support?</h2>
          <p className="text-muted-foreground mb-6">
            Our technical team is available to help with device specifications, compatibility, and integration support.
          </p>
          <div className="space-x-4">
            <a href="/contact-truecore" className="tech-btn-primary">
              Contact Technical Team
            </a>
            <a href="mailto:support@truecoregadgets.com" className="tech-btn-outline">
              Email Support
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TechnicalSpecs;
